import React, { useState } from 'react';

import useSittingStore from '../../../stores/sittingStore';

const EMPTY_NOTES = Object.freeze([]);
const PREVIEW_LIMIT = 120;

function truncate(text, max = PREVIEW_LIMIT) {
  if (!text) return { text: '', truncated: false };
  const s = String(text);
  if (s.length <= max) return { text: s, truncated: false };
  return { text: `${s.slice(0, max - 1).trimEnd()}…`, truncated: true };
}

function roleLabel(role) {
  if (!role) return '';
  const r = String(role);
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
}

function formatStamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = (a, b) => (
      a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
    );
    const time = d.toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    if (sameDay(d, today)) return time;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

/**
 * NoteHoverPopover — non-blocking preview popover for procedure notes,
 * shown on hover/focus of its child trigger (the chip's "📝 Notes (N)"
 * button). Click on the trigger still routes through to whatever click
 * handler the child has (opens ProcedureNoteDialog for the full thread).
 *
 * Renders the trigger only — no popover — when the sitting has no
 * notes, so empty chips don't grow a phantom hover target.
 *
 * Long notes are truncated to 120 chars with an `…` suffix; a footer
 * hint tells the doctor that a click opens the full thread.
 */
export default function NoteHoverPopover({ sittingId, children }) {
  const notes = useSittingStore(
    (s) => (sittingId ? (s.notesBySitting?.[sittingId] || EMPTY_NOTES) : EMPTY_NOTES),
  );
  const [open, setOpen] = useState(false);

  if (notes.length === 0) {
    return children;
  }

  const recent = notes.slice(-2);
  const anyTruncated = recent.some((n) => (n.body || '').length > PREVIEW_LIMIT);
  const hiddenCount = notes.length - recent.length;

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && (
        <div
          role="tooltip"
          className="bg-white text-slate-900 border-slate-300
                     dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            width: 280,
            maxWidth: '80vw',
            padding: '0.5rem 0.625rem',
            borderRadius: 8,
            borderWidth: 1,
            borderStyle: 'solid',
            boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
            zIndex: 50,
            pointerEvents: 'none',
            fontSize: '0.75rem',
            lineHeight: 1.35,
          }}
        >
          <div
            className="text-indigo-700 dark:text-indigo-300"
            style={{ fontWeight: 600, marginBottom: 4 }}
          >
            💬 {notes.length} note{notes.length === 1 ? '' : 's'}
          </div>
          {recent.map((n) => {
            const author = n.author || {};
            const { text } = truncate(n.body);
            return (
              <div key={n.note_id} style={{ marginBottom: 6 }}>
                <div
                  className="text-slate-500 dark:text-slate-400"
                  style={{ fontSize: '0.6875rem', marginBottom: 1 }}
                >
                  {author.name || 'Unknown'}
                  {author.role ? ` · ${roleLabel(author.role)}` : ''}
                  {n.created_at ? ` · ${formatStamp(n.created_at)}` : ''}
                </div>
                <div
                  className="text-slate-800 dark:text-slate-200"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {text}
                </div>
              </div>
            );
          })}
          {(anyTruncated || hiddenCount > 0) && (
            <div
              className="text-slate-500 dark:text-slate-400"
              style={{
                marginTop: 4,
                paddingTop: 4,
                borderTop: '1px solid rgba(15,23,42,0.08)',
                fontSize: '0.6875rem',
                fontStyle: 'italic',
              }}
            >
              {hiddenCount > 0
                ? `+${hiddenCount} more · click to view full thread`
                : 'Click to view full thread'}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
